import { useState, useCallback, useRef, useEffect } from 'react';

type FeedbackState = 'idle' | 'loading' | 'success' | 'error';

interface UseButtonFeedbackOptions {
  /** How long to show the success/error state before reverting (ms). Default: 2000 */
  duration?: number;
}

interface ButtonFeedbackResult {
  /** Current feedback state */
  state: FeedbackState;
  /** Call when the action starts (sets loading state) */
  onLoading: () => void;
  /** Call when the action succeeds (flashes success, then reverts to idle) */
  onSuccess: () => void;
  /** Call when the action fails (flashes error, then reverts to idle) */
  onError: () => void;
  /** Reset to idle immediately */
  reset: () => void;
  /** Whether the button is in success state */
  isSuccess: boolean;
  /** Whether the button is in error state */
  isError: boolean;
  /** Whether the button is loading */
  isLoading: boolean;
}

/**
 * Hook that manages button feedback state (idle → loading → success/error → idle).
 * Use this to flash a button green/red after an action completes.
 *
 * @example
 * ```tsx
 * const feedback = useButtonFeedback();
 *
 * const handleClick = async () => {
 *   feedback.onLoading();
 *   try {
 *     await doSomething();
 *     feedback.onSuccess();
 *   } catch {
 *     feedback.onError();
 *   }
 * };
 *
 * <Button
 *   color={feedback.isSuccess ? 'success' : feedback.isError ? 'error' : 'primary'}
 *   startIcon={feedback.isSuccess ? <Check /> : undefined}
 *   disabled={feedback.isLoading}
 * >
 *   {feedback.isSuccess ? t('common.done') : 'Save'}
 * </Button>
 * ```
 */
export function useButtonFeedback(options?: UseButtonFeedbackOptions): ButtonFeedbackResult {
  const { duration = 2000 } = options ?? {};
  const [state, setState] = useState<FeedbackState>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const revertAfterDelay = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      setState('idle');
      timerRef.current = null;
    }, duration);
  }, [duration]);

  const onLoading = useCallback(() => {
    clearTimer();
    setState('loading');
  }, []);

  const onSuccess = useCallback(() => {
    setState('success');
    revertAfterDelay();
  }, [revertAfterDelay]);

  const onError = useCallback(() => {
    setState('error');
    revertAfterDelay();
  }, [revertAfterDelay]);

  const reset = useCallback(() => {
    clearTimer();
    setState('idle');
  }, []);

  return {
    state,
    onLoading,
    onSuccess,
    onError,
    reset,
    isSuccess: state === 'success',
    isError: state === 'error',
    isLoading: state === 'loading',
  };
}
